import React, { useMemo, useState } from 'react';
import {
  Button,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoney, formatQuantity } from '@/utils/format';
import { buildCbsPathMap } from '@/utils/cbsPathUtils';
import { resourceSummary } from '@/utils/wbsLibraryTemplateComponents';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';
import { EpcWbsLibraryCostTemplateDrawer } from './EpcWbsLibraryCostTemplateDrawer';

const { Text } = Typography;

const QUICK_PRESETS = [
  { cbs: 'MAT', kind: 'MATERIAL', label: '+ Material' },
  { cbs: 'LAB', kind: 'LABOUR', label: '+ Labour' },
  { cbs: 'EQP', kind: 'EQUIPMENT', label: '+ Equipment' },
  { cbs: 'SUB', kind: 'SUBCONTRACT', label: '+ Subcontract' },
] as const;

export interface EpcWbsLibraryCostTemplateGridProps {
  packageId: string;
  libraryNodeId: string;
  nodeLabel?: string;
  readOnly?: boolean;
}

export const EpcWbsLibraryCostTemplateGrid: React.FC<EpcWbsLibraryCostTemplateGridProps> = ({
  packageId,
  libraryNodeId,
  nodeLabel,
  readOnly,
}) => {
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [inlineId, setInlineId] = useState<string | null>(null);

  const costsQ = useQuery({
    queryKey: ['epc-wbs-lib-costs', packageId, libraryNodeId],
    queryFn: () => epcWbsLibraryService.listCostTemplates(packageId, libraryNodeId),
    enabled: !!packageId && !!libraryNodeId,
  });

  const cbsQ = useQuery({
    queryKey: ['epc-cbs-codes'],
    queryFn: () => epcWbsLibraryService.listCbsCodes(),
  });

  const labourQ = useQuery({
    queryKey: ['epc-wbs-lib-labour-classes'],
    queryFn: () => epcWbsLibraryService.listLabourResourceClasses(),
  });

  const equipmentQ = useQuery({
    queryKey: ['epc-wbs-lib-equipment-classes'],
    queryFn: () => epcWbsLibraryService.listEquipmentResourceClasses(),
  });

  const rows = Array.isArray(costsQ.data) ? costsQ.data : [];
  const cbsNodes = useMemo(
    () =>
      (cbsQ.data || []).map((c: any) => ({
        id: String(c.id),
        code: String(c.code),
        name: c.name,
        parent_id: c.parent_id,
      })),
    [cbsQ.data]
  );
  const cbsPathMap = useMemo(() => buildCbsPathMap(cbsNodes), [cbsNodes]);

  const cbsByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of cbsQ.data || []) m.set(String(c.code), String(c.id));
    return m;
  }, [cbsQ.data]);

  const lineTotal = useMemo(
    () =>
      rows.reduce(
        (sum, r) => sum + (Number(r.default_quantity) || 0) * (Number(r.default_rate) || 0),
        0
      ),
    [rows]
  );

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-costs'] });
    void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-cost-templates'] });
    void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-readiness'] });
    void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-readiness-batch'] });
    void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-analytics'] });
  };

  const createM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcWbsLibraryService.createCostTemplate(packageId, { ...v, libraryNodeId }),
    onSuccess: () => {
      message.success('Template added');
      setDrawerOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchM = useMutation({
    mutationFn: (p: { id: string } & Record<string, unknown>) =>
      epcWbsLibraryService.patchCostTemplate(packageId, p.id, p),
    onSuccess: () => {
      message.success('Template updated');
      setEditing(null);
      setDrawerOpen(false);
      setInlineId(null);
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => epcWbsLibraryService.deleteCostTemplate(packageId, id),
    onSuccess: () => {
      message.success('Template removed');
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const quickAdd = (preset: (typeof QUICK_PRESETS)[number]) => {
    const cbsId = cbsByCode.get(preset.cbs);
    if (!cbsId) {
      message.warning(`CBS code ${preset.cbs} not found — add it in Company CBS`);
      return;
    }
    const existing = rows.find((r) => String(r.cbs_code) === preset.cbs);
    if (existing) {
      setEditing(existing);
      setDrawerOpen(true);
      return;
    }
    createM.mutate({
      cbsCodeId: cbsId,
      resourceKind: preset.kind,
      description: preset.label.replace('+ ', ''),
      defaultQuantity: 1,
      defaultRate: 0,
      uom: preset.kind === 'LABOUR' ? 'HOUR' : preset.kind === 'EQUIPMENT' ? 'DAY' : undefined,
    });
  };

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setDrawerOpen(true);
  };

  const saveInline = (row: any, field: string, value: number | string) => {
    patchM.mutate({ id: String(row.id), [field]: value });
  };

  const submitDrawer = (payload: Record<string, unknown>) => {
    if (editing) patchM.mutate({ id: String(editing.id), ...payload });
    else createM.mutate(payload);
  };

  return (
    <div>
      {!readOnly ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <Space wrap size={4}>
            {QUICK_PRESETS.map((p) => (
              <Button key={p.cbs} size="small" onClick={() => quickAdd(p)}>
                {p.label}
              </Button>
            ))}
          </Space>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add template
          </Button>
        </div>
      ) : null}

      <Table
        size="small"
        rowKey="id"
        loading={costsQ.isLoading}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 780 }}
        columns={[
          {
            title: 'CBS',
            width: 140,
            fixed: 'left',
            render: (_, row) => cbsPathMap.get(String(row.cbs_code_id)) || row.cbs_code,
          },
          { title: 'Kind', dataIndex: 'resource_kind', width: 88, ellipsis: true },
          {
            title: 'Resources',
            width: 110,
            render: (_, row) => {
              const label = resourceSummary(row);
              return label ? <Tag>{label}</Tag> : <Text type="secondary">—</Text>;
            },
          },
          {
            title: 'Description',
            dataIndex: 'description',
            ellipsis: true,
            render: (v, row) =>
              readOnly || inlineId !== String(row.id) ? (
                <span
                  style={{ cursor: readOnly ? undefined : 'pointer' }}
                  onClick={() => !readOnly && setInlineId(String(row.id))}
                >
                  {v}
                </span>
              ) : (
                <Input
                  size="small"
                  defaultValue={v}
                  autoFocus
                  onBlur={(e) => {
                    if (e.target.value !== v) saveInline(row, 'description', e.target.value);
                    else setInlineId(null);
                  }}
                  onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                />
              ),
          },
          {
            title: 'Qty',
            dataIndex: 'default_quantity',
            width: 88,
            render: (v, row) =>
              readOnly ? (
                formatQuantity(v)
              ) : (
                <InputNumber
                  size="small"
                  min={0}
                  defaultValue={Number(v)}
                  key={`${row.id}-qty-${v}`}
                  style={{ width: '100%' }}
                  onBlur={(e) => {
                    const raw = (e.target as HTMLInputElement).value;
                    const val = Number(raw);
                    if (!Number.isNaN(val) && val !== Number(v)) {
                      saveInline(row, 'defaultQuantity', val);
                    }
                  }}
                />
              ),
          },
          { title: 'UOM', dataIndex: 'uom', width: 56 },
          {
            title: 'Rate',
            dataIndex: 'default_rate',
            width: 100,
            render: (v, row) =>
              readOnly ? (
                formatMoney(v, { display: 'plain', maximumFractionDigits: 2 })
              ) : (
                <InputNumber
                  size="small"
                  min={0}
                  defaultValue={Number(v)}
                  key={`${row.id}-rate-${v}`}
                  style={{ width: '100%' }}
                  onBlur={(e) => {
                    const raw = (e.target as HTMLInputElement).value;
                    const val = Number(raw);
                    if (!Number.isNaN(val) && val !== Number(v)) {
                      saveInline(row, 'defaultRate', val);
                    }
                  }}
                />
              ),
          },
          {
            title: 'Amount',
            width: 96,
            fixed: 'right',
            render: (_, row) =>
              formatMoney(
                (Number(row.default_quantity) || 0) * (Number(row.default_rate) || 0),
                { display: 'plain', maximumFractionDigits: 0 }
              ),
          },
          ...(readOnly
            ? []
            : [
                {
                  title: '',
                  width: 64,
                  fixed: 'right' as const,
                  render: (_: unknown, row: any) => (
                    <Space size={0}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(row)}
                      />
                      <Popconfirm
                        title="Remove template?"
                        onConfirm={() => deleteM.mutate(String(row.id))}
                      >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]),
        ]}
      />

      <div style={{ marginTop: 8, textAlign: 'right' }}>
        <Text type="secondary">Package node total: </Text>
        <Text strong>
          {formatMoney(lineTotal, { display: 'plain', maximumFractionDigits: 0 })}
        </Text>
      </div>

      <EpcWbsLibraryCostTemplateDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        editing={editing}
        cbsNodes={cbsNodes}
        labourClasses={labourQ.data || []}
        equipmentClasses={equipmentQ.data || []}
        nodeLabel={nodeLabel}
        pending={createM.isPending || patchM.isPending}
        onSubmit={submitDrawer}
      />
    </div>
  );
};
